import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../../../Contexts/TenantContext";
import { productService } from "../../../Api/Services/productService";
import { productBrandService } from "../../../Api/Services/productBrandService";
import { productCategoryService } from "../../../Api/Services/productCategoryService";
import { productModelService } from "../../../Api/Services/productModelService";
import { SimplifiedUserProduct } from "../../../Models/Product";
import { ProductBrand } from "../../../Models/ProductBrand";
import { ProductCategory } from "../../../Models/ProductCategory";
import { ProductModel } from "../../../Models/ProductModel";
import { toPublicPath } from "../../../Utils/publicRoutes";
import { matchListCase } from "../../../Utils/DataFormatters";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import ProductCard from "../../../Components/ProductCard/ProductCard";
import Footer from "../../../Components/Footer/Footer";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import Pagination from "../../../Components/Pagination/Pagination";
import PaginationSummary from "../../../Components/Pagination/PaginationSummary";

const UserProductPage = () => {
  const { tenantPreference } = useTenant();
  const enableBrandsFeature = tenantPreference?.enableBrandsFeature ?? true;
  const enableCategoriesFeature =
    tenantPreference?.enableCategoriesFeature ?? true;
  const enableModelsFeature = tenantPreference?.enableModelsFeature ?? true;
  const location = useLocation();

  const navigate = useNavigate();

  const [brands, setBrands] = useState<ProductBrand[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [models, setModels] = useState<ProductModel[]>([]);
  const [products, setProducts] = useState<SimplifiedUserProduct[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [sortOption, setSortOption] = useState<string>("default");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const productsPerPage = 12;
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryId = params.get("categoria");

    if (categoryId) {
      setSelectedCategoryId(categoryId);
    } else {
      setSelectedCategoryId(null);
    }
    setCurrentPage(1);
    setFiltersReady(true);
  }, [location.search]);

  useEffect(() => {
    loadBrands();
    loadCategories();
    loadModels();
  }, []);

  useEffect(() => {
    sortProducts(sortOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, selectedCategoryId, selectedBrandId, selectedModelId]);

  useEffect(() => {
    if (!filtersReady) {
      return;
    }
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersReady,
    currentPage,
    selectedBrandId,
    selectedCategoryId,
    selectedModelId,
    search,
  ]);

  const loadBrands = async () => {
    const data = await productBrandService.getAll();
    setBrands(data);
  };

  const loadCategories = async () => {
    const data = await productCategoryService.getAll();
    setCategories(data);
  };

  const loadModels = async () => {
    const data = await productModelService.getAll();
    setModels(data);
  };

  const loadProducts = async () => {
    setIsLoading(true);

    if (!hasLoadedOnce) {
      // reset current list to avoid showing stale data/pagination during first fetch
      setProducts([]);
      setTotalItems(0);
      setTotalPages(1);
    }

    const data = await productService.getAllActive({
      page: currentPage,
      pageSize: productsPerPage,
      categoryId: selectedCategoryId ?? null,
      brandId: selectedBrandId ?? null,
      modelId: selectedModelId ?? null,
      search,
    });

    setProducts(data.items);
    setTotalPages(data.totalPages);
    setTotalItems(data.totalItems);
    setIsLoading(false);
    setHasLoadedOnce(true);
  };

  const handleSelectCategory = (id: string | null) => {
    setSelectedCategoryId(id);
    setSortOption("default");
    setCurrentPage(1);
    const basePath = toPublicPath("/produtos");
    navigate(id ? `${basePath}?categoria=${id}` : basePath, { replace: true });
  };

  const handleSelectBrand = (id: string | null) => {
    setSelectedBrandId(id);
    setCurrentPage(1);
  };

  const handleSelectModel = (id: string | null) => {
    setSelectedModelId(id);
    setCurrentPage(1);
  };

  const handleSearch = (term: string) => {
    setSearch(term);
    setCurrentPage(1);
  };

  const sortProducts = (option: string) => {
    const sorted = [...products];

    switch (option) {
      case "latest":
        sorted.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "price-asc":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "name-asc":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        return;
    }

    setProducts(sorted);
  };

  const SkeletonBlock = ({
    height = 16,
    width = "100%",
  }: {
    height?: number | string;
    width?: number | string;
  }) => (
    <div
      className="animate-pulse bg-gray-200 rounded w-full"
      style={{ height, maxWidth: width }}
    />
  );

  const renderSkeleton = () => (
    <div
      className={`flex flex-col mx-auto page-gutter py-8 gap-6 sm:gap-8 md:flex-row w-full max-w-[76.5rem] sm:py-24`}
    >
      {/* DESKTOP FILTERS SKELETON */}
      <aside
        className={`hidden md:flex w-full flex-col gap-6 sm:w-64`}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="space-y-3">
            <SkeletonBlock height={20} width={160} />
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((__, innerIndex) => (
                <SkeletonBlock
                  key={innerIndex}
                  height={14}
                  width={`${70 + innerIndex * 10}%`}
                />
              ))}
            </div>
          </div>
        ))}
      </aside>

      {/* MAIN CONTENT SKELETON */}
      <main className="flex flex-col flex-1 items-center sm:block w-full space-y-6">
        <div className="w-full flex flex-col sm:flex-row sm:justify-between gap-3">
          <SkeletonBlock height={28} width={200} />
          <SkeletonBlock height={40} width={200} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 w-full">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="space-y-3">
              <SkeletonBlock height={180} />
              <SkeletonBlock height={16} width="80%" />
              <SkeletonBlock height={16} width="60%" />
              <SkeletonBlock height={14} width="50%" />
            </div>
          ))}
        </div>

        <SkeletonBlock height={16} width={200} />
      </main>
    </div>
  );

  const renderGridSkeleton = () => (
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 w-full">
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="space-y-3">
          <SkeletonBlock height={180} />
          <SkeletonBlock height={16} width="80%" />
          <SkeletonBlock height={16} width="60%" />
          <SkeletonBlock height={14} width="50%" />
        </div>
      ))}
    </div>
  );

  const showFullSkeleton = isLoading && !hasLoadedOnce;
  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  const allCategoriesLabel = matchListCase("Todos os produtos", categories.map((category) => category.name));
  const allBrandsLabel = matchListCase("Todas as marcas", brands.map((brand) => brand.name));
  const allModelsLabel = matchListCase("Todos os modelos", models.map((model) => model.name));

  return (
    <div>
      <SmartNavbar />
      {showFullSkeleton ? (
        renderSkeleton()
      ) : (
        <div
          className={`flex flex-col mx-auto page-gutter py-8 gap-6 sm:gap-8 md:flex-row w-full max-w-[76.5rem] sm:py-24`}
        >
          {/* DESKTOP FILTERS */}
          <aside
            className={`hidden md:flex w-full flex-col divide-y sm:w-64 [&>*]:py-6 [&>*:first-child]:pt-0`}
          >
            <div>
              <h2 className="text-2xl font-bold mb-4">
                {"PESQUISA"}
              </h2>
              <SearchBar
                className="w-full sm:pl-3"
                onSearch={handleSearch}
                value={search}
              />
            </div>
            {enableCategoriesFeature && (
              <div>
                <h2 className="text-2xl font-bold mb-4">
                  {"CATEGORIAS"}
                </h2>
                <ul className="space-y-2">
                  <li
                    onClick={() => handleSelectCategory(null)}
                    className={`cursor-pointer text-md ${
                      selectedCategoryId === null
                        ? "text-primary-600 font-bold"
                        : "text-gray-700 hover:text-primary-600"
                    }`}
                  >
                    {allCategoriesLabel}
                  </li>
                  {categories.map((cat) => (
                    <li
                      key={cat.id}
                      onClick={() => handleSelectCategory(cat.id)}
                      className={`cursor-pointer text-md ${
                        selectedCategoryId === cat.id
                          ? "text-primary-600 font-bold"
                          : "text-gray-700 hover:text-primary-700"
                      }`}
                    >
                      {cat.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {enableModelsFeature && models.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-4">
                  {"MODELOS"}
                </h2>
                <ul className="space-y-2">
                  <li
                    onClick={() => handleSelectModel(null)}
                    className={`cursor-pointer text-md ${
                      selectedModelId === null
                        ? "text-primary-600 font-bold"
                        : "text-gray-700 hover:text-primary-600"
                    }`}
                  >
                    {allModelsLabel}
                  </li>
                  {models.map((model) => (
                    <li
                      key={model.id}
                      onClick={() => handleSelectModel(model.id)}
                      className={`cursor-pointer text-md ${
                        selectedModelId === model.id
                          ? "text-primary-600 font-bold"
                          : "text-gray-700 hover:text-primary-700"
                      }`}
                    >
                      {model.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {enableBrandsFeature && (
              <div>
                <h2 className="text-2xl font-bold mb-4">
                  {"MARCAS"}
                </h2>
                <ul className="space-y-2">
                  <li
                    onClick={() => handleSelectBrand(null)}
                    className={`cursor-pointer text-md ${
                      selectedBrandId === null
                        ? "text-primary-600 font-bold"
                        : "text-gray-700 hover:text-primary-600"
                    }`}
                  >
                    {allBrandsLabel}
                  </li>
                  {brands.map((brand) => (
                    <li
                      key={brand.id}
                      onClick={() => handleSelectBrand(brand.id)}
                      className={`cursor-pointer text-md ${
                        selectedBrandId === brand.id
                          ? "text-primary-600 font-bold"
                          : "text-gray-700 hover:text-primary-700"
                      }`}
                    >
                      {brand.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          {/* MOBILE FILTER PANEL */}
          <details className="md:hidden mb-4 border rounded p-3">
            <summary className="font-medium cursor-pointer">FILTROS</summary>

            <div className="mt-4 space-y-6">
              {enableCategoriesFeature && (
                <div>
                  {/* CATEGORY FILTER */}
                  <h3 className="text-sm font-semibold mb-2">Categorias</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleSelectCategory(null)}
                      className={`px-3 py-1 rounded-full text-sm border ${
                        selectedCategoryId === null
                          ? "bg-primary-600 text-white border-primary-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                      }`}
                    >
                      {allCategoriesLabel}
                    </button>

                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleSelectCategory(cat.id)}
                        className={`px-3 py-1 rounded-full text-sm border ${
                          selectedCategoryId === cat.id
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {enableBrandsFeature && (
                <div>
                  {/* BRAND FILTER */}
                  <h3 className="text-sm font-semibold mb-2">Marcas</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleSelectBrand(null)}
                      className={`px-3 py-1 rounded-full text-sm border ${
                        selectedBrandId === null
                          ? "bg-primary-600 text-white border-primary-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                      }`}
                    >
                      {allBrandsLabel}
                    </button>

                    {brands.map((brand) => (
                      <button
                        key={brand.id}
                        onClick={() => handleSelectBrand(brand.id)}
                        className={`px-3 py-1 rounded-full text-sm border ${
                          selectedBrandId === brand.id
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                        }`}
                      >
                        {brand.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* MODEL FILTER */}
              {enableModelsFeature && models.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Modelos</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleSelectModel(null)}
                      className={`px-3 py-1 rounded-full text-sm border ${
                        selectedModelId === null
                          ? "bg-primary-600 text-white border-primary-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                      }`}
                    >
                      {allModelsLabel}
                    </button>

                    {models.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => handleSelectModel(model.id)}
                        className={`px-3 py-1 rounded-full text-sm border ${
                          selectedModelId === model.id
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                        }`}
                      >
                        {model.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </details>

          {/* MAIN CONTENT */}
          <main className="flex flex-col flex-1 items-center sm:block w-full">
            <div className="w-full mb-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <h1 className="text-2xl font-bold break-words">
                  {selectedCategory?.name ?? "PRODUTOS"}
                </h1>
                {products.length > 0 && (
                  <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="border px-3 py-2 rounded w-full text-sm sm:w-auto sm:text-md sm:flex-shrink-0"
                  >
                    <option value="default">Ordenação padrão</option>
                    <option value="latest">Ordenar por mais recentes</option>
                    <option value="price-asc">Preço: menor para maior</option>
                    <option value="price-desc">Preço: maior para menor</option>
                    <option value="name-asc">Nome: A a Z</option>
                    <option value="name-desc">Nome: Z a A</option>
                  </select>
                )}
              </div>
              {selectedCategory?.description?.trim() && (
                <p className="mt-2 max-w-3xl whitespace-pre-line text-sm leading-6 text-gray-700 sm:text-[0.95rem]">
                  {selectedCategory.description.trim()}
                </p>
              )}
            </div>

            {isLoading && products.length === 0 ? (
              renderGridSkeleton()
            ) : products.length === 0 ? (
              <p className="w-full min-h-[320px] sm:min-h-[480px] text-center sm:text-left text-gray-500">Nenhum produto encontrado.</p>
            ) : (
              // Keep the current page visible while the next one loads so the
              // page height holds and the scroll to the top starts from here
              <div
                className={`grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 transition-opacity ${
                  isLoading ? "opacity-60 pointer-events-none" : ""
                }`}
              >
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    id={product.id}
                    name={product.name}
                    code={product.code}
                    price={product.price}
                    plusPrice={product.plusPrice}
                    availableSizes={product.availableSizes}
                    originalPrice={product.originalPrice}
                    discountPercentage={product.discountPercentage}
                    stockQuantity={product.stockQuantity}
                    isMadeToOrder={product.isMadeToOrder}
                    origin={product.origin}
                    mainImageUrl={product.mainImageUrl}
                  />
                ))}
              </div>
            )}

            {totalItems > 0 && products.length > 0 && (
              <>
                <PaginationSummary
                  totalItems={totalItems}
                  singularLabel="produto encontrado"
                  pluralLabel="produtos encontrados"
                />
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            )}
          </main>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default UserProductPage;
